import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import config from "../../../../config";
import { toast } from "react-toastify";
import { toastAttr } from "../../OfflineLeads/utils";
import { FAQEditor } from "./cityfunctions";
const CityEditor = () => {
  const [cities, setCities] = useState([]);
  const [mandapTypes, setMandapTypes] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedMandapTypeId, setSelectedMandapTypeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [editorReady, setEditorReady] = useState(false);
  const [faq, setFAQ] = useState([]);
  const editor = useRef(null);

  // Fetch cities
  useEffect(() => {
    let cancel = false;
    const fetchCities = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${config.service}/masters/city/`);
        if (!cancel) {
          setCities(data.map((c) => ({ label: c.name, value: c.name })));
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load cities.", toastAttr);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    const fetchMandapTypes = async () => {
      try {
        const { data } = await axios.get(
          `${config.service}/masters/mandap_type_count/`
        );
        setMandapTypes(data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load mandap types.", toastAttr);
      }
    };
    fetchCities();
    fetchMandapTypes();
    return () => (cancel = true);
  }, []);

  // Initialize EditorJS
  useEffect(() => {
    let editorInstance;
    let cancel = false;

    const initEditor = async () => {
      try {
        const EditorJS = (await import("@editorjs/editorjs")).default;
        const Header = (await import("@editorjs/header")).default;
        const Paragraph = (await import("@editorjs/paragraph")).default;
        const List = (await import("@editorjs/list")).default;
        const ImageTool = (await import("@editorjs/image")).default;

        editorInstance = new EditorJS({
          holder: "editor",
          placeholder: "✍️ Start writing...",
          autofocus: true,
          tools: {
            header: { class: Header, inlineToolbar: true },
            paragraph: { class: Paragraph, inlineToolbar: true },
            list: { class: List, inlineToolbar: true },
            image: {
              class: ImageTool,
              config: {
                uploader: {
                  async uploadByFile(file) {
                    const formData = new FormData();
                    formData.append("image", file);
                    const res = await axios.post(
                      `${config.service}/masters/aws-image-to-urls/`,
                      formData,
                      { headers: { "Content-Type": "multipart/form-data" } }
                    );
                    return { success: 1, file: { url: res.data.url } };
                  },
                  async uploadByUrl(url) {
                    return { success: 1, file: { url } };
                  },
                },
              },
            },
          },
          // Remove default content - start with empty editor
          data: { blocks: [] },
        });

        if (!cancel) {
          editor.current = editorInstance;
          setEditorReady(true);
        }
      } catch (err) {
        console.error("Editor init error:", err);
        toast.error("Failed to initialize editor.", toastAttr);
      }
    };

    initEditor();

    return () => {
      cancel = true;
      if (editorInstance?.destroy) editorInstance.destroy();
    };
  }, []);

  useEffect(() => {
    if (!selectedCity || !editorReady || !editor.current) return;
    let cancel = false;

    const loadCityContent = async () => {
      try {
        setLoading(true);

        // Build params dynamically
        const params = { city_name: selectedCity };
        if (selectedMandapTypeId) {
          params.mandap_type_id = selectedMandapTypeId;
        }

        const { data } = await axios.get(
          `${config.service}/masters/mandap/city-content-v2/`,
          { params }
        );

        if (cancel) return;

        console.log("API Response:", data.data);

        setMetaTitle(data.data.metaTitle || "");
        setIntro(data.data.intro || "");
        setFAQ(data.data.faqs || []);

        // If no HTML content exists, clear the editor and return
        if (!data.data.html_content) {
          await editor.current.clear();
          setLoading(false);
          return;
        }

        const html = data.data.html_content;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const blocks = [];

        const proseContainer = doc.querySelector(".prose");
        const contentRoot = proseContainer || doc.body;

        const getCleanText = (element) =>
          element.textContent?.trim() || element.innerText?.trim() || "";

        contentRoot.childNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // Skip non-elements
          const tagName = node.tagName.toLowerCase();

          if (tagName === "figure") {
            const img = node.querySelector("img");
            if (img && img.src) {
              const caption =
                node.querySelector("figcaption")?.textContent?.trim() || "";
              blocks.push({
                type: "image",
                data: {
                  file: { url: img.src },
                  caption,
                  withBorder: false,
                  withBackground: false,
                  stretched: false,
                },
              });
            }
            return;
          }

          const textContent = getCleanText(node);
          if (!textContent) return;

          if (tagName.startsWith("h")) {
            const level = parseInt(tagName[1]);
            blocks.push({
              type: "header",
              data: { text: textContent, level: level || 2 },
            });
          } else if (tagName === "p") {
            blocks.push({ type: "paragraph", data: { text: textContent } });
          } else if (tagName === "ul" || tagName === "ol") {
            const items = Array.from(node.querySelectorAll("li"))
              .map((li) => getCleanText(li))
              .filter((t) => t.length > 0);
            if (items.length > 0) {
              blocks.push({
                type: "list",
                data: {
                  style: tagName === "ol" ? "ordered" : "unordered",
                  items,
                },
              });
            }
          } else {
            blocks.push({ type: "paragraph", data: { text: textContent } });
          }
        });

        console.log("Converted blocks:", blocks);

        await editor.current.clear();
        if (blocks.length > 0) {
          await editor.current.render({ blocks });
        } else {
          await editor.current.render({
            blocks: [
              {
                type: "header",
                data: { text: `Content for ${selectedCity}`, level: 2 },
              },
              {
                type: "paragraph",
                data: { text: "Start editing your city content here..." },
              },
            ],
          });
        }
      } catch (err) {
        console.error("Error loading city content:", err);
        if (err.response?.status !== 404) {
          toast.error("Failed to load city content.", toastAttr);
        }
        if (editor.current) await editor.current.clear();
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    loadCityContent();
    return () => (cancel = true);
  }, [selectedCity, editorReady, selectedMandapTypeId]);

  // Save content
  const handleSave = async () => {
    console.debug("[handleSave] Triggered for city:", selectedCity);

    // Early validation with better user feedback
    if (!editor.current) {
      console.warn("[handleSave] Editor is not ready.");
      toast.error("Editor not ready. Please try again.", toastAttr);
      return;
    }

    if (!selectedCity) {
      console.warn("[handleSave] No city selected.");
      toast.error("Please select a city first.", toastAttr);
      return;
    }

    try {
      setSaving(true);
      console.debug("[handleSave] Saving state set to true.");

      const output = await editor.current.save();
      console.debug("[handleSave] Raw editor output:", output);

      if (!output?.blocks?.length) {
        console.warn("[handleSave] No blocks returned from editor.");
        toast.error(
          "No content to save. Please add some content first.",
          toastAttr
        );
        return;
      }

      // Use array mapping for better performance and readability
      const htmlContent = generateHTMLFromBlocks(output.blocks, selectedCity);
      console.debug("[handleSave] Generated HTML content:", htmlContent);


      console.debug("[handleSave] Sending POST request with payload:", {
        mandap_type_id: selectedMandapTypeId,
        city_name: selectedCity,
        metaTitle: metaTitle || `Best Halls in ${selectedCity}`,
        intro: intro || `Find the best venues in ${selectedCity}`,
        html_content: htmlContent,
      });

      const response = await axios.post(
        `${config.service}/masters/mandap/city-content-v2-create/`,
        {
          city_name: selectedCity,
          mandap_type_id: selectedMandapTypeId,
          metaTitle: metaTitle || `Marriage Halls in ${selectedCity}`,
          intro: intro || `Find the best venues in ${selectedCity}`,
          html_content: htmlContent,
          faqs: faq,
        }
      );

      console.debug("[handleSave] Server response:", response);

      toast.success(
        `✅ Content successfully saved for ${selectedCity}`,
        toastAttr
      );
    } catch (err) {
      console.error("[handleSave] Save error:", err);
      toast.error(
        `Save failed: ${
          err.response?.data?.message || err.message || "Unknown error"
        }`,
        toastAttr
      );
    } finally {
      setSaving(false);
      console.debug("[handleSave] Saving state set to false.");
    }
  };

  // Separate function for HTML generation (reusable and testable)
  const escapeHTML = (str) => {
    if (typeof str !== "string") {
      console.warn("[escapeHTML] Non-string input:", str);
      if (Array.isArray(str)) str = str.join(" ");
      else if (str == null) str = "";
      else str = String(str);
    }
    return str.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  };

  const generateHTMLFromBlocks = (blocks, selectedCity) => {
    console.debug("[generateHTMLFromBlocks] Blocks received:", blocks);

    const blockHandlers = {
      header: (block) => {
        console.debug("[generateHTMLFromBlocks] Handling header:", block);
        return block.data?.text
          ? `<h2 class="text-2xl font-bold text-gray-800 mb-5 leading-tight">${escapeHTML(
              block.data.text
            )}</h2>`
          : "";
      },

      paragraph: (block) => {
        console.debug("[generateHTMLFromBlocks] Handling paragraph:", block);
        return block.data?.text
          ? `<p class="text-md leading-relaxed text-gray-700 mb-4 max-w-none prose-p:text-gray-700">${escapeHTML(
              block.data.text
            )}</p>`
          : "";
      },

      list: (block) => {
        console.debug("[generateHTMLFromBlocks] Handling list:", block);
        if (!Array.isArray(block.data?.items) || !block.data.items.length) {
          console.warn(
            "[generateHTMLFromBlocks] List block has no items:",
            block
          );
          return "";
        }

        const isOrdered = block.data.style === "ordered";
        const tag = isOrdered ? "ol" : "ul";
        const listClass = isOrdered
          ? "list-decimal list-inside mb-6 space-y-2 pl-4 marker:text-blue-600"
          : "list-disc list-inside mb-6 space-y-2 pl-4 marker:text-blue-600";

        const listItems = block.data.items
          .map((item) => {
            let text;
            if (typeof item === "string") {
              text = item;
            } else if (item && typeof item === "object") {
              // Try common properties used by plugins
              text = item.text || item.content || JSON.stringify(item);
            } else {
              text = "";
            }
            console.debug("[generateHTMLFromBlocks] List item text:", text);
            return text
              ? `<li class="text-base md:text-lg leading-relaxed text-gray-700 mb-2 pl-2">${escapeHTML(
                  text
                )}</li>`
              : "";
          })
          .join("");

        return `<${tag} class="${listClass}">${listItems}</${tag}>`;
      },

      image: (block) => {
        console.debug("[generateHTMLFromBlocks] Handling image:", block);
        if (!block.data?.file?.url) {
          console.warn(
            "[generateHTMLFromBlocks] Image block missing URL:",
            block
          );
          return "";
        }

        const caption = block.data.caption
          ? `<figcaption class="text-sm text-gray-600 mt-3 text-center italic leading-relaxed">${escapeHTML(
              block.data.caption
            )}</figcaption>`
          : "";

        return `
        <figure class="mb-8 ">
          <img 
            src="${escapeHTML(block.data.file.url)}" 
            alt="${escapeHTML(
              block.data.caption || `Image of ${selectedCity}`
            )}" 
            class="w-full h-auto rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 object-cover"
            loading="lazy"
          />
          ${caption}
        </figure>
      `;
      },
    };

    // Safely process all blocks
    const contentHTML = blocks
      .map((block) => {
        if (!block || !block.type) {
          console.warn("[generateHTMLFromBlocks] Invalid block:", block);
          return "";
        }
        const handler = blockHandlers[block.type];
        if (!handler) {
          console.warn(
            "[generateHTMLFromBlocks] Unknown block type:",
            block.type
          );
          return "";
        }
        return handler(block);
      })
      .filter(Boolean)
      .join("");

    return `
    <div class="px-2 py-8">
      <div class="prose prose-lg prose-gray max-w-none">
        ${contentHTML}
      </div>
    </div>
  `;
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 20 }}>
      <select
        value={selectedCity}
        onChange={(e) => setSelectedCity(e.target.value)}
        disabled={loading}
        style={{ width: "100%", marginBottom: 20, padding: 10 }}
      >
        <option value="">--Select City--</option>
        {cities.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
      <select
        value={selectedMandapTypeId}
        onChange={(e) => setSelectedMandapTypeId(e.target.value)}
        disabled={loading}
        style={{ width: "100%", marginBottom: 20, padding: 10 }}
      >
        <option value="">--Select Mandap Type--</option>
        {mandapTypes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.mandap_type}
          </option>
        ))}
      </select>

      {/* <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          value={metaTitle}
          onChange={(e) => setMetaTitle(e.target.value)}
          placeholder="Meta Title"
          style={{ width: "100%", padding: 10, marginBottom: 10 }}
        />

        <textarea
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          placeholder="Intro"
          style={{ width: "100%", padding: 10, minHeight: 80 }}
        />
      </div> */}

      <div style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            Loading content...
          </div>
        )}
        <div
          id="editor"
          style={{
            textTransform: "none",
            border: "1px solid #ccc",
            minHeight: 400,
            padding: 10,
            background: "#fff",
          }}
        ></div>
      </div>
      <div style={{ marginTop: 30 }}>
        <FAQEditor data={faq} onChange={setFAQ} />
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !selectedCity}
        style={{
          marginTop: 20,
          padding: "10px 20px",
          backgroundColor: saving ? "#6c757d" : "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: saving ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Saving..." : "Save Content"}
      </button>
    </div>
  );
};

export default CityEditor;
